import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Beacon } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { FETCH_BEACONS_QUERY_KEY } from './devices-query-keys';
import { addOrUpdateBeaconCache } from './devices-cache';

export function useFetchBeacons(companyId: string): {
    status: 'error' | 'success' | 'pending';
    beacons: Beacon[] | undefined;
    refetch: () => void;
} {
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    const { status, data, refetch } = useQuery({
        queryKey: [FETCH_BEACONS_QUERY_KEY, companyId],
        queryFn: () => {
            return openApiRef.current.devices
                .getBeacons({ companyId })
                .then((response) => {
                    const beacons = response.data.data.beacons.data;
                    // Seed the per-beacon caches this read already paid for.
                    beacons.forEach((beacon) => {
                        addOrUpdateBeaconCache(queryClient, beacon);
                    });
                    return beacons;
                });
        }
    });

    return {
        status,
        beacons: data,
        refetch: () => {
            void refetch();
        }
    };
}
